import { createVariadicExpression } from '../expressions';
import { operatorDefinitions } from '../operators';

export const sanitizeBuilderQuery = (
  originalBuilderQuery: Record<string, unknown>,
): Record<string, unknown> => {
  if (!originalBuilderQuery || !Array.isArray(originalBuilderQuery?.operands))
    return originalBuilderQuery;

  const builderQuery = JSON.parse(JSON.stringify(originalBuilderQuery));

  type Row = {
    expressionType: string;
    left: Record<string, unknown> & {
      source: string;
      sourceName: string;
      fieldTarget: string;
    };
    right: Record<string, unknown> & {
      value: string;
      type: string;
      direction?: string;
      operands: Record<string, unknown> &
        {
          fieldTarget: string;
          source: string;
          sourceName: string;
        }[];
    };
    operands: Record<string, unknown> &
      {
        fieldTarget: string;
        source: string;
        sourceName: string;
      }[];
  };

  let newTree: Row[] = [];

  // check all of the rows for formatting issues
  for (let i = 0, len = builderQuery.operands.length; i < len; i += 1) {
    const row = { ...builderQuery.operands[i] };

    // correctly format source information
    switch (row.expressionType) {
      case 'binary':
        // handle phoneType and campaign sources specifically as no target is returned on subsequent loads
        if (
          row?.left?.fieldTarget === 'phoneType' ||
          row?.left?.source === 'phoneType'
        ) {
          row.left = {
            ...row.left,
            source: 'phoneType',
            sourceName: 'phoneType',
          };
        } else if (
          row?.left?.fieldTarget === 'campaign' ||
          row?.left?.source === 'campaign'
        ) {
          row.left = {
            ...row.left,
            source: 'campaign',
            sourceName: 'campaign',
          };
        } else {
          row.left = {
            ...row.left,
            source: row.left.fieldTarget,
            sourceName: row.left.source,
          };
        }
        delete row.left.fieldTarget;
        break;
      case 'unary':
        row.operands[0] =
          row.operands[0].fieldTarget === 'campaign' ||
          row.operands[0].source === 'campaign'
            ? { ...row.operands[0], source: 'campaign', sourceName: 'campaign' }
            : {
                ...row.operands[0],
                source: row.operands[0].fieldTarget,
                sourceName: row.operands[0].source,
              };
        delete row.operands[0].fieldTarget;
        break;
      default:
        break;
    }

    newTree = [...newTree, row];
  }

  // check every 'row' of the builderQuery for formatting issues
  for (let i = 0, len = newTree.length; i < len; i += 1) {
    const originalRow = newTree[i];
    let row = { ...originalRow };

    // do we need to transform a binary rows relativeDate?
    if (
      originalRow.expressionType === 'binary' &&
      originalRow?.right.type === 'relativeDate'
    ) {
      const currentDateValue = Math.abs(parseInt(row.right.value, 10));

      if (!Number.isNaN(currentDateValue)) {
        row.right = {
          ...row.right,
          value:
            row.right.direction === 'ago'
              ? (currentDateValue * -1).toString()
              : currentDateValue.toString(),
        };
        delete row.right.direction;
      }
    }

    // do we need to transform binary row into a variadic row?
    if (
      originalRow.expressionType === 'binary' &&
      originalRow.right?.expressionType === 'variadic'
    ) {
      // we need to convert this row into multiple rows
      const multipleRows = originalRow.right.operands.map((op) => ({
        ...originalRow,
        right: op,
      }));

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      row = createVariadicExpression(operatorDefinitions.or, ...multipleRows);
    }

    newTree[i] = row;
  }

  return { ...builderQuery, operands: newTree };
};
